import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../api.js";
import {
  ONBOARDING_TIMEZONES,
  canSubmit,
  fieldErrorsFrom,
  formFromView,
  formatFileSize,
  onboardingStep,
  patchFromForm,
  type OnboardingForm
} from "../onboarding-model.js";
import { CenteredCard } from "./RestaurantPicker.js";
import { logout } from "../auth.js";
import { ErrorPage, errorDetails } from "../error-page.js";
import {
  CheckCircleIcon,
  ClockIcon,
  FileIcon,
  Hint,
  MailIcon,
  PhoneIcon,
  SendIcon,
  SignOutIcon,
  StoreIcon,
  TrashIcon,
  UploadIcon,
  WithIcon
} from "../icons.js";

/**
 * Onboarding: the step between signing up and having a restaurant.
 *
 * Progress is saved on the server rather than held in this component, so closing the tab and
 * coming back resumes where they left off — the reason the application has a DRAFT state at all.
 */
export function OnboardingPage() {
  return (
    <>
      <SignOutCorner />
      <OnboardingContent />
    </>
  );
}

/**
 * Sign out, on every state of this page: loading, unavailable, the form and "you're all set".
 *
 * This page sits outside the restaurant layout, whose header is the only other place with a sign-out button,
 * so a new owner here (or someone who signed up with the wrong address) had no way out. Pinned to the corner
 * rather than added to the card, because the card is shared with the signed-out sign-in screen.
 */
export function SignOutCorner() {
  return (
    <div className="signout-corner">
      <button className="ghost with-icon" onClick={() => void logout()}>
        <SignOutIcon size={16} />
        <span>Sign out</span>
      </button>
    </div>
  );
}

function OnboardingContent() {
  const queryClient = useQueryClient();

  // POST rather than GET on mount: this both reads and creates-if-absent, so a freshly signed-up
  // owner lands on a usable form instead of an empty state telling them to do something.
  const application = useQuery({
    queryKey: ["application"],
    queryFn: api.startOrResumeApplication,
    retry: false
  });

  const step = onboardingStep(application.data);

  if (application.isPending) return <CenteredCard>Loading…</CenteredCard>;

  if (application.error) {
    return <ErrorPage details={errorDetails(application.error, { context: "onboarding: load application" })} />;
  }

  if (step === "submitted") {
    return <SubmittedCard nextStep={application.data?.nextStep} />;
  }

  return (
    <ApplicationForm
      initial={formFromView(application.data)}
      missing={application.data?.missing ?? []}
      onSaved={() => void queryClient.invalidateQueries({ queryKey: ["application"] })}
    />
  );
}

function SubmittedCard({ nextStep }: { nextStep?: string }) {
  return (
    <CenteredCard>
      <h1>
        <WithIcon icon={<CheckCircleIcon size={22} />}>You’re all set</WithIcon>
      </h1>
      <p className="muted">{nextStep ?? "We’ll be in touch shortly."}</p>
      <p className="muted">
        <WithIcon icon={<MailIcon size={16} />}>We’ll email you when you’re ready to take calls.</WithIcon>
        <Hint>A person sets up your phone number and checks your menu, so this part isn’t instant.</Hint>
      </p>
    </CenteredCard>
  );
}

function ApplicationForm({
  initial,
  missing,
  onSaved
}: {
  initial: OnboardingForm;
  missing: readonly string[];
  onSaved: () => void;
}) {
  const [form, setForm] = useState<OnboardingForm>(initial);
  const [submitProblems, setSubmitProblems] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string>();
  const queryClient = useQueryClient();

  // Only the FIRST server value seeds the inputs. Re-seeding on every refetch would overwrite
  // whatever the applicant is typing at the moment a background save resolves.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    setForm(initial);
  }, [initial]);

  const save = useMutation({
    mutationFn: () => api.saveApplication(patchFromForm(form)),
    onSuccess: () => {
      setSaveError(undefined);
      onSaved();
    },
    onError: (err: unknown) =>
      setSaveError(err instanceof ApiError ? err.message : "Could not save your details")
  });

  const submit = useMutation({
    mutationFn: async () => {
      // Save first: the server validates what it has STORED, so an unsaved field would be reported
      // as missing even though the applicant can see it on screen.
      await api.saveApplication(patchFromForm(form));
      return api.submitApplication();
    },
    onSuccess: () => {
      setSubmitProblems([]);
      void queryClient.invalidateQueries({ queryKey: ["application"] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setSubmitProblems(err.problems.length > 0 ? err.problems : [err.message]);
        return;
      }
      setSubmitProblems(["Could not submit your application"]);
    }
  });

  const shown = submitProblems.length > 0 ? submitProblems : missing;
  const parsed = fieldErrorsFrom(shown);
  const other = parsed.other;
  /*
   * Field errors only after a submit was refused. On first load the server's list of what is still missing
   * used to paint the empty time-zone field red before anyone had touched it; the note under the disabled
   * button already says what is needed. Shown in the user's terms rather than the API's.
   */
  const fields =
    submitProblems.length > 0
      ? {
          restaurantName: parsed.fields.restaurantName && "Enter your restaurant’s name.",
          timezone: parsed.fields.timezone && "Choose a time zone."
        }
      : {};
  const set = (key: keyof OnboardingForm) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <CenteredCard>
      <h1>
        Tell us about your restaurant
        <Hint>We use this to set up your phone agent. Your answers are saved, so you can come back later.</Hint>
      </h1>

      <Field icon={<StoreIcon size={15} />} label="Restaurant name" error={fields.restaurantName}>
        <input
          value={form.restaurantName}
          onChange={(e) => set("restaurantName")(e.target.value)}
          onBlur={() => save.mutate()}
          placeholder="Sichuan Impression"
          autoComplete="organization"
        />
      </Field>

      <Field icon={<ClockIcon size={15} />} label="Time zone" error={fields.timezone}>
        <select
          value={form.timezone}
          onChange={(e) => {
            set("timezone")(e.target.value);
            // A select has no meaningful blur, so save on change.
            setTimeout(() => save.mutate(), 0);
          }}
        >
          <option value="">Choose…</option>
          {ONBOARDING_TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </Field>

      <Field
        icon={<PhoneIcon size={15} />}
        label="Current phone number (optional)"
        hint="The number customers already call. We only use it to set up forwarding; we never route calls to it."
      >
        <input
          value={form.existingPhoneE164}
          onChange={(e) => set("existingPhoneE164")(e.target.value)}
          onBlur={() => save.mutate()}
          placeholder="+15551234567"
          autoComplete="tel"
        />
      </Field>

      <Field icon={<PhoneIcon size={15} />} label="Preferred area code (optional)" hint="For the new number we set up for you.">
        <input
          value={form.preferredAreaCode}
          onChange={(e) => set("preferredAreaCode")(e.target.value)}
          onBlur={() => save.mutate()}
          placeholder="626"
          inputMode="numeric"
        />
      </Field>

      <MenuUploads />

      {saveError ? <p className="error">{saveError}</p> : null}
      {other.map((problem) => (
        <p className="error" key={problem}>
          {problem}
        </p>
      ))}

      <button
        className="primary wide"
        disabled={!canSubmit(form) || submit.isPending}
        onClick={() => submit.mutate()}
      >
        <WithIcon icon={<SendIcon size={16} />}>{submit.isPending ? "Submitting…" : "Finish setup"}</WithIcon>
      </button>
      {!canSubmit(form) ? <p className="muted small">Needs a name and time zone.</p> : null}
    </CenteredCard>
  );
}

function Field({
  icon,
  label,
  hint,
  error,
  children
}: {
  icon?: React.ReactNode;
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">
        {icon ? <WithIcon icon={icon}>{label}</WithIcon> : label}
        {hint ? <Hint>{hint}</Hint> : null}
      </span>
      {children}
      {error ? <span className="field-error">{error}</span> : null}
    </label>
  );
}

/**
 * Menu attachments.
 *
 * The list comes from the server on every change rather than being tracked locally, because storage
 * is the source of truth for what was actually uploaded.
 */
function MenuUploads() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  const uploads = useQuery({
    queryKey: ["application-uploads"],
    queryFn: api.listApplicationUploads,
    retry: false
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["application-uploads"] });

  const add = useMutation({
    mutationFn: async (files: FileList) => {
      // Sequential, not parallel: each presign counts existing files against the per-application
      // cap, so concurrent requests would all see the old count and overshoot it.
      for (const file of Array.from(files)) await api.uploadMenuFile(file);
    },
    onSuccess: () => {
      setError(undefined);
      refresh();
    },
    onError: (err: unknown) => {
      // The server's messages are written for the applicant ("larger than 15MB", "PDF or a photo"),
      // so they are shown as-is rather than replaced with something vaguer.
      setError(err instanceof ApiError ? err.message : "That file couldn’t be uploaded");
      refresh(); // an earlier file in the batch may have succeeded
    }
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => api.deleteApplicationUpload(fileId),
    onSuccess: refresh
  });

  const files = uploads.data?.files ?? [];

  return (
    <div className="field">
      <span className="field-label">
        <WithIcon icon={<UploadIcon size={15} />}>Your menu</WithIcon>
        <Hint>A PDF or photos of each page. Clear photos are fine; we’ll type it up for you.</Hint>
      </span>

      <input
        ref={inputRef}
        type="file"
        aria-label="Add menu files"
        multiple
        accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={(e) => {
          const chosen = e.target.files;
          if (chosen && chosen.length > 0) add.mutate(chosen);
          // Clear the input so choosing the same file twice still fires a change event.
          e.target.value = "";
        }}
        disabled={add.isPending}
      />
      {add.isPending ? <span className="muted">Uploading…</span> : null}
      {error ? <span className="error">{error}</span> : null}

      {files.length > 0 ? (
        <ul className="upload-list">
          {files.map((file) => (
            <li key={file.fileId}>
              <WithIcon icon={<FileIcon size={15} />}>{file.filename}</WithIcon>
              <span className="muted"> {formatFileSize(file.sizeBytes)}</span>
              <button
                className="link"
                onClick={() => remove.mutate(file.fileId)}
                disabled={remove.isPending}
                aria-label={`Remove ${file.filename}`}
                title="Remove"
              >
                <TrashIcon size={15} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <span className="muted small">Nothing attached yet. You can send it later.</span>
      )}
    </div>
  );
}
